import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CUSTOMS_MISSED_MARK_ROLES } from '@crm/shared'
import { getCustomsSendReport, markCustomsMissed } from '../../shared/api/customs.api'
import { apiErrorText } from '../../shared/api/errorText'
import { toast } from '../../shared/ui/toastStore'
import { useAuthStore } from '../auth/authStore'
import type { AnalyticsPeriodParams } from '../../shared/api/analytics.api'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { EmptyState } from '../../shared/ui/EmptyState'
import { CustomsSendsRequests } from './CustomsSendsRequests'

/**
 * Вкладка «Нетарифка» «Аналитики» (17.10.2026): какие позиции из-за рубежа
 * менеджеры отправляли логисту. Три уровня: цифры периода → менеджеры → их
 * заявки → позиции заявки. Нужна РОПу и админу, чтобы найти позицию, которую
 * не отправили, а при закупке всплыла пошлина или разрешительные документы.
 * Поиск по партномеру — без периода, по всем заявкам: «чья это позиция».
 * РОП и админ отмечают у неотправленной позиции «Нужна была отправка» — на
 * таможне всплыла пошлина или документы; счётчик таких — по менеджерам.
 */

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('ru-RU')

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'green' | 'red' }) {
  const color =
    tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-700' : 'text-gray-900'
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${color}`}>{value}</div>
    </div>
  )
}

function SentBar({ sent, total }: { sent: number; total: number }) {
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-block h-1.5 w-20 rounded bg-gray-200">
        <span className="block h-1.5 rounded bg-green-600" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-gray-800">
        {sent} · {pct} %
      </span>
    </span>
  )
}

export function CustomsSendsTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customs-send-report', periodParams, search],
    queryFn: () => getCustomsSendReport({ ...periodParams, search }),
    // Пока ищется новое — показываем прошлое, строка поиска не теряет фокус.
    placeholderData: (prev) => prev,
  })
  const role = useAuthStore((s) => s.user?.role)
  const canMark = !!role && (CUSTOMS_MISSED_MARK_ROLES as string[]).includes(role)
  const queryClient = useQueryClient()
  const mark = useMutation({
    mutationFn: (v: { itemId: string; missed: boolean }) => markCustomsMissed([v.itemId], v.missed),
    onSuccess: (res, v) => {
      void queryClient.invalidateQueries({ queryKey: ['customs-send-report'] })
      if (res.changed === 0) toast.error('Отметка не изменилась: позицию уже отправляли логисту')
      else toast.success(v.missed ? 'Отмечено: нужна была отправка' : 'Отметка снята')
    },
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось изменить отметку')),
  })
  const markProps = canMark
    ? {
        onMarkMissed: (itemId: string, missed: boolean) => mark.mutate({ itemId, missed }),
        markingItemId: mark.isPending ? (mark.variables?.itemId ?? null) : null,
      }
    : {}
  const [managerId, setManagerId] = useState<string | null>(null)
  const [onlyUnsent, setOnlyUnsent] = useState(true)

  const requests = useMemo(() => {
    const list = (data?.requests ?? []).filter((r) => r.managerId === managerId)
    return onlyUnsent ? list.filter((r) => r.sent < r.total) : list
  }, [data, managerId, onlyUnsent])

  const searchBox = (
    <input
      type="search"
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
      placeholder="Найти по партномеру — во всех заявках, без учёта периода"
      aria-label="Поиск по партномеру"
      className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
    />
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        {searchBox}
        <PageLoader label="Загружаю отчёт по нетарифке…" />
      </div>
    )
  }
  if (isError || !data) return <ErrorMessage error="Не удалось загрузить отчёт по нетарифке" />
  if (!data.since) {
    return (
      <EmptyState
        title="Отправок логисту ещё не было"
        description="Отчёт появится, когда менеджеры начнут отправлять позиции кнопкой «Отправить брокеру»."
      />
    )
  }

  const { totals } = data
  const pct = totals.total > 0 ? Math.round((totals.sent / totals.total) * 100) : 0
  const manager = data.managers.find((m) => m.managerId === managerId) ?? null

  if (search) {
    return (
      <div className="space-y-4">
        {searchBox}
        <CustomsSendsRequests
          title={`Найдено по «${search}»`}
          requests={data.requests}
          showManager
          expandAll
          {...markProps}
          emptyText="Позиций из-за рубежа с таким партномером нет"
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {searchBox}
      <p className="text-xs text-gray-500">
        Позиции «Китай», «Производители», «Стоки» в заявках периода: отправлял ли их менеджер
        логисту кнопкой «Отправить брокеру». Заявки считаются с {fmtDate(data.since)} — с первой
        отправки логисту. «Нужна была отправка» — позицию не отправили, а на таможне по ней всплыла
        пошлина или документы (отмечают РОП и админ).
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Позиций из-за рубежа" value={String(totals.total)} />
        <Kpi label="Отправлено логисту" value={`${totals.sent} · ${pct} %`} tone="green" />
        <Kpi
          label="Не отправлено"
          value={String(totals.notSent)}
          tone={totals.notSent ? 'red' : undefined}
        />
        <Kpi label="Заявок с неотправленными" value={String(totals.requestsWithUnsent)} />
        <Kpi
          label="Нужна была отправка"
          value={String(totals.missed)}
          tone={totals.missed ? 'red' : undefined}
        />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Менеджеры</h3>
        <p className="mt-0.5 text-xs text-gray-500">Нажмите на менеджера — откроются его заявки.</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium">Менеджер</th>
                <th className="px-3 py-2 text-right font-medium">Заявок</th>
                <th className="px-3 py-2 text-right font-medium">Позиций из-за рубежа</th>
                <th className="px-3 py-2 font-medium">Отправлено</th>
                <th className="px-3 py-2 text-right font-medium">Не отправлено</th>
                <th className="py-2 pl-3 text-right font-medium">Нужна была отправка</th>
              </tr>
            </thead>
            <tbody>
              {data.managers.map((m) => {
                const open = m.managerId === managerId
                return (
                  <tr
                    key={m.managerId}
                    onClick={() => setManagerId(open ? null : m.managerId)}
                    className={`cursor-pointer border-b border-gray-50 hover:bg-gray-50 ${open ? 'bg-brand-50' : ''}`}
                    data-testid="customs-report-manager"
                  >
                    <td className="py-2 pr-3 font-medium text-gray-800">
                      {open ? '▾' : '▸'} {m.managerName ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{m.requests}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{m.total}</td>
                    <td className="px-3 py-2">
                      <SentBar sent={m.sent} total={m.total} />
                    </td>
                    <td
                      className={`px-3 py-2 text-right ${m.notSent > 0 ? 'font-medium text-red-600' : 'text-gray-400'}`}
                    >
                      {m.notSent}
                    </td>
                    <td
                      className={`py-2 pl-3 text-right ${m.missed > 0 ? 'font-semibold text-red-700' : 'text-gray-400'}`}
                    >
                      {m.missed}
                    </td>
                  </tr>
                )
              })}
              {data.managers.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-sm text-gray-400">
                    За период нет заявок с позициями из-за рубежа
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {manager && (
        <CustomsSendsRequests
          title={`Заявки: ${manager.managerName ?? '—'}`}
          requests={requests}
          emptyText={onlyUnsent ? 'Неотправленных позиций нет' : 'Заявок нет'}
          {...markProps}
          onlyUnsent={onlyUnsent}
          onOnlyUnsentChange={setOnlyUnsent}
        />
      )}
    </div>
  )
}
